<template>
  <!--
      @pointerdown.stop: mismo motivo que en FeaturePopup.vue. Este panel
      además tiene su propia asa nativa de resize (CSS resize:horizontal en
      .feature-side-panel más abajo) — arrastrarla también empieza con un
      pointerdown, que sin este guard alcanzaba a disparar el singleclick
      fantasma de OpenLayers (con la coordenada de donde soltaste el mouse al
      terminar de resizear) y sobreescribía la selección real.
  -->
  <div class="feature-side-panel" @pointerdown.stop>
    <div class="expanded-header">
      <div class="expanded-title">
        <h5 :title="panelTitle">{{ panelTitle }}</h5>
        <span v-if="featureTitle" class="expanded-subtitle" :title="getFeatureLayerName(currentFeature)">{{ getFeatureLayerName(currentFeature) }}</span>
      </div>
      <div class="buttons">
        <button
          v-if="features.length > 1"
          class="header-btn"
          :disabled="currentIndex === 0"
          @click="prevFeature"
          title="Anterior"
        >
          <font-awesome-icon icon="chevron-left" />
        </button>
        <span v-if="features.length > 1" class="expanded-count">{{ currentIndex + 1 }} / {{ features.length }}</span>
        <button
          v-if="features.length > 1"
          class="header-btn"
          :disabled="currentIndex === features.length - 1"
          @click="nextFeature"
          title="Siguiente"
        >
          <font-awesome-icon icon="chevron-right" />
        </button>
        <button class="header-btn" @click="restore" title="Restaurar">
          <font-awesome-icon icon="compress" />
        </button>
        <button class="header-btn" @click="close" title="Cerrar">
          <font-awesome-icon icon="times" />
        </button>
      </div>
    </div>
    <div class="expanded-content">
      <div v-if="currentFeature">
        <table class="table table-striped table-sm expanded-attributes-table">
          <tbody>
            <tr v-for="(value, key) in displayProperties" :key="key">
              <td class="attr-key">{{ key }}</td>
              <td class="attr-value">
                <AttachmentGallery
                  v-if="isTerrainAttachmentValue(value)"
                  :attachment-ids="value.attachmentIds"
                  :attachment-names="value.attachmentNames"
                  :kind="value.kind"
                  :label="value.label"
                />
                <span v-else v-html="sanitizeHtml(value)"></span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="no-data">
        <p>No se encontraron resultados.</p>
      </div>
    </div>
  </div>
</template>

<script>
// FeaturePopupExpanded.vue — panel lateral de detalle de FeaturePopup.vue,
// abierto desde su botón "Ampliar". Comparte selectService.state
// (features/currentIndex) para mostrar siempre la MISMA feature que el
// popup chico, montado condicionalmente por DataContainer.vue. Es un panel
// acoplado a un borde de la pantalla (no flotante/arrastrable como
// GeoprocessingChart.vue): el ancho se ajusta con el asa de resize nativa
// del borde izquierdo (resize: horizontal), sin necesitar draggableDiv.
import { selectService, getFeatureLayerName, getFeatureProperties, getFeatureTitle, isTerrainAttachmentValue } from '@/services/map/selectService';
import AttachmentGallery from '@/components/terrain/AttachmentGallery.vue';
import { sanitizeHtml } from '@/services/helpers/displayValueHelper';

export default {
  name: 'FeaturePopupExpanded',
  components: { AttachmentGallery },
  computed: {
    features() {
      return selectService.state.features;
    },
    currentIndex() {
      return selectService.state.currentIndex;
    },
    currentFeature() {
      return this.features.length ? this.features[this.currentIndex] : null;
    },
    displayProperties() {
      return getFeatureProperties(this.currentFeature);
    },
    // QA-206: mismo criterio que FeaturePopup.vue — el campo configurado como
    // `titulo` manda y el nombre de la capa baja a subtítulo.
    featureTitle() {
      return getFeatureTitle(this.currentFeature);
    },
    panelTitle() {
      return this.featureTitle || getFeatureLayerName(this.currentFeature);
    },
  },
  methods: {
    getFeatureLayerName,
    isTerrainAttachmentValue,
    sanitizeHtml,
    nextFeature() {
      if (this.currentIndex < this.features.length - 1) selectService.setCurrentIndex(this.currentIndex + 1);
    },
    prevFeature() {
      if (this.currentIndex > 0) selectService.setCurrentIndex(this.currentIndex - 1);
    },
    restore() {
      selectService.setMaximized(false);
    },
    close() {
      selectService.clearResults();
    },
  },
};
</script>

<style scoped lang="scss">
.feature-side-panel {
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: 30vw;
  background: white;
  z-index: 5000;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 340px;
  max-width: 45vw;
  // Asa nativa para ajustar el ancho arrastrando el borde izquierdo — un
  // panel lateral no se arrastra de lugar (siempre pegado al borde), solo
  // se ensancha/angosta.
  resize: horizontal;
  font-family: 'Inter', sans-serif;
}

.expanded-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.6em 0.8em;
  background: #006fb3;
  border-bottom: 1px solid #004b7a;
  color: white;
  flex-shrink: 0;

  h5 {
    margin: 0;
    font-weight: 700;
    font-size: 1rem;
    color: white;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

// min-width:0 deja que el título se encoja dentro del header flex en vez de
// empujar los botones fuera del panel.
.expanded-title {
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin-right: 10px;
}

// QA-206: nombre de la capa como contexto bajo el título configurado.
.expanded-subtitle {
  margin-top: 2px;
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.85);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.buttons {
  display: flex;
  flex-wrap: nowrap;
  flex-shrink: 0;
  align-items: center;
  gap: 4px;
}

.expanded-count {
  color: white;
  font-size: 0.8rem;
  white-space: nowrap;
}

.header-btn {
  border: 1px solid #ddd;
  background: white;
  color: #006fb3;
  border-radius: 4px;
  transition: 0.2s ease;
  padding: 4px 8px;
  font-size: 0.85rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;

  &:hover:not(:disabled) {
    background: #e8f4fb;
    border-color: #006fb3;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.expanded-content {
  flex: 1;
  overflow-y: auto;
  padding: 1em;
}

.expanded-attributes-table {
  margin-bottom: 0;
  font-size: 0.9rem;
  width: 100%;
  table-layout: fixed;

  td {
    padding: 8px 10px;
    border-bottom: 1px solid #e2e8f0;
    // Sin truncar acá: esta es justamente la vista para ver el texto largo
    // completo — se ajusta a varias líneas en vez de cortarse.
    word-break: break-word;
    vertical-align: top;
  }

  .attr-key {
    font-weight: 600;
    color: #475569;
    width: 35%;
    background-color: #f1f5f9;
  }

  .attr-value {
    color: #1e293b;
    width: 65%;
  }
}

.no-data {
  color: #64748b;
  font-style: italic;
  text-align: center;
  padding: 10px;
}
</style>
